/**
 * Field Manager — map display and interaction for crop-monitoring fields.
 *
 * `fields$` is viewport-filtered (centroid inside padded bounds); markers, clusters,
 * and polygons reflect that subset only.
 *
 * Zoom [12, polygonThreshold): centroid markers + Leaflet.markercluster.
 * Zoom ≥ polygonThreshold: white-outline polygons (geojson-vt fill) + clustered centroid markers.
 */
import { Injectable, NgZone, inject } from '@angular/core';
import geojsonvt from 'geojson-vt';
import {clearLeafletDataPane} from './gis-vt-pane-cleanup.util';
import { TranslateService } from '@ngx-translate/core';
import { FieldService, Field } from '@6grain/masofa-monolith-api-client/crop-monitoring';
import { OpenStreetMapComponent } from '@components/openstreetmap/openstreetmap.component';
import { Observable, BehaviorSubject, Subject, Subscription } from 'rxjs';
import { debounceTime, finalize, map, tap } from 'rxjs/operators';
import { PolygonConverterUtil } from '../../../../../utils/polygon-converter.util';
import { GisCircleMarker } from '@app/pages/electronic-field-map/pages/gis-fields/interfaces/gis-component.interfaces';
import { createRegionCircleMarkers } from '@app/utils/circle-marker.util';
import { RegionManagerService } from '@components/openstreetmap/managers/region-manager.service';
import { CropCacheService } from '@services/caches/crop-cache.service';
import { findFeatureAtPoint, latLngToTileCoords, latLngToTilePixel } from '@app/utils/vector-tile.utils';
import { MapClickEvent, MapMoveEvent, Marker } from '@components/openstreetmap/interfaces';
import { filterByViewportBounds } from './gis-map-viewport-filter.util';
import { escapePopupHtml } from './gis-popup-html.util';
import { buildVtOriginalFeaturesByNumericId } from './gis-vt-feature-index.util';
import { paintVtPolygonOnCanvasTile, repaintLeafletGridLayerCanvases } from './gis-vt-polygon-canvas.util';
import { OSM_POLYGON_FILL_VISIBILITY_EVENT } from '@components/openstreetmap/constants/map-polygon-fill-visibility.state';
import { FieldsByRegionsDto } from '@6grain/masofa-monolith-api-client/crop-monitoring/model/fieldsByRegionsDto';
import { FirmSmallDto } from '@6grain/masofa-monolith-api-client/crop-monitoring/model/firmSmallDto';
import { buildGroundSurveyPinDivIconHtml } from '@app/pages/crop-monitoring/pages/gis-bids/request/bid-map-html.util';
import { parseWktPoint } from '@app/utils/wkt-parser';
import { Router } from '@angular/router';

declare var L: any;

const FIELD_VT_TILE_EXTENT = 4096;

/** Extra padding on map bounds when culling markers/polygons for the viewport. */
const FIELD_VIEWPORT_BOUNDS_PAD = 0.08;

/** Accent used for field markers, region summary circles, and polygon fill (see `gis-fields.scss` for cluster styling). */
const FIELD_LAYER_ACCENT_RGBA = 'rgba(156, 195, 26, 1)';

/** The manufacturer legal-address pin — the only firm marker on the fields map. */
const FIRM_PIN_COLOR = '#ff0000';
const FIRM_PIN_PANE = 'firmMarkersPane';
/** Above raster overlays (NDVI, weather) and the fields VT pane (905). */
const FIRM_PIN_PANE_Z_INDEX = 1000;
const FIRM_PIN_POPUP_LINK_CLASS = 'firm-map-card-link';

/** Manufacturer card route, opened from the pin popup link. */
const FIRM_CARD_ROUTE = '/dictionaries/firms/edit';

/** `map-layers.controller` reads `pane` off marker data; it is not part of the closed `Marker` interface. */
type PanedMarker = Marker & { pane?: string };

/** Geojson-vt canvas fill; stroke is white. */
const FIELD_POLYGON_FILL = FIELD_LAYER_ACCENT_RGBA;
const FIELD_POLYGON_STROKE = '#ffffff';
const FIELD_GEOJSON_VT_OPTIONS = {
  maxZoom: 18,
  extent: FIELD_VT_TILE_EXTENT,
  buffer: 64,
  lineMetrics: false,
  promoteId: 'fieldId' as const,
  generateId: false,
  indexMaxZoom: 5,
  indexMaxPoints: 100000,
} as const;

@Injectable({ providedIn: 'root' })
export class FieldManager {
  private translate = inject(TranslateService);
  public fieldService = inject(FieldService);
  private mapInstance: OpenStreetMapComponent | null = null;
  private regionManager = inject(RegionManagerService);
  private cropCacheService = inject(CropCacheService);
  private ngZone = inject(NgZone);

  private lastNameFilterValue: string | null = null;

  // Store data
  public fields$ = new BehaviorSubject<Field[]>([]);

  // Events
  public fieldMarkerClick$ = new Subject<{ lat: number; lng: number; data: any }>();
  public fieldPolygonClick$ = new Subject<any>();
  /** Named markercluster group for multi-layer GIS. Empty = default group. */
  clusterGroupId: string | null = null;
  /** Leaflet pane for clustered field markers (General GIS uses one pane per layer stack rank). */
  markerClusterPane: string | null = null;
  private accentColor = FIELD_LAYER_ACCENT_RGBA;
  polygonVtFillOpacity: number | null = null;
  polygonVtStrokeWidth: number | null = null;
  polygonVtStrokeDashArray: number[] | null = null;
  /** Leaflet pane for geojson-vt field polygons (General GIS may override per layer stack). */
  vectorTilesPane = 'fieldsVectorTilesPane';

  setAccentColor(color: string): void {
    if (color) {
      this.accentColor = color;
    }
  }

  /** Opacity 0–100 for polygon fill. Repaints existing canvas tiles in place (no layer rebuild). */
  setPolygonFillOpacity(opacityPercent: number): void {
    this.polygonVtFillOpacity = Math.max(0, Math.min(100, Number(opacityPercent) || 0)) / 100;
    this.repaintFieldsVtCanvases();
  }

  /** Same zoom bands as SeasonManager (markers vs polygons). */
  private static readonly ZOOM_DETAIL_MIN = 12;
  private static readonly ZOOM_POLYGONS_ONLY_MIN = 14;
  private currentZoom: number = 13;
  /** Below this zoom: markers only; at/above: geojson-vt polygons + markers. */
  private zoomThreshold: number = FieldManager.ZOOM_POLYGONS_ONLY_MIN;

  /** Centroid marker ids this manager added (polygons use a separate VT layer). */
  private managedMarkerIds: Set<string> = new Set();

  /** Leaflet canvas grid + hit-test index for field polygons. */
  private fieldsVtLayer: any = null;
  private fieldsVtPointer: {
    tileIndex: any;
    featuresById: Map<number, any>;
  } | null = null;
  private fieldsVtThinStroke = false;

  /** Bumps when VT layer is cleared so stale async tile builds are ignored. */
  private fieldsVtOpId = 0;

  private fieldsDisplaySub?: Subscription;
  private fieldsVtMapClickSub?: Subscription;
  /** Debounced map move + immediate zoom for tiered redraw. */
  private mapViewportRedrawSub?: Subscription;

  private polygonFillLeafletMap: any = null;

  /** Firm id of the manufacturer legal-address pin currently on the map. */
  private firmPinFirmId: string | null = null;
  /** Leaflet map the pin popup link handler is attached to. */
  private firmPinPopupHandlerMap: any = null;

  constructor(private router: Router) {}

  private readonly onOsmPolygonFillVisibility = (): void => {
    try {
      this.fieldsVtLayer?.redraw?.();
    } catch {
      /* ignore */
    }
  };

  /**
   * Public setter to allow external components to override threshold (e.g., set to 11)
   */
  public setZoomThreshold(threshold: number): void {
    try {
      this.zoomThreshold = Number(threshold) || this.zoomThreshold;
      // Re-evaluate display after threshold change
      this.updateDisplay();
    } catch (err) {
      console.warn('Failed to set zoom threshold', err);
    }
  }

  /**
   * Initialize the manager with a map instance
   */
  initialize(mapInstance: OpenStreetMapComponent): void {
    this.mapViewportRedrawSub?.unsubscribe();
    this.mapViewportRedrawSub = undefined;

    this.mapInstance = mapInstance;
    this.setupViewportRedrawListeners();

    const leafletMap = mapInstance.getMapInstance?.();
    if (this.polygonFillLeafletMap && this.polygonFillLeafletMap !== leafletMap) {
      try {
        this.polygonFillLeafletMap.off(OSM_POLYGON_FILL_VISIBILITY_EVENT, this.onOsmPolygonFillVisibility);
      } catch {
        /* ignore */
      }
    }
    this.polygonFillLeafletMap = leafletMap ?? null;
    try {
      leafletMap?.on?.(OSM_POLYGON_FILL_VISIBILITY_EVENT, this.onOsmPolygonFillVisibility);
    } catch {
      /* ignore */
    }

    if (!this.fieldsDisplaySub) {
      this.fieldsDisplaySub = this.fields$.subscribe(() => {
        this.updateDisplay();
      });
    }

    if (!this.fieldsVtMapClickSub && mapInstance?.mapClick$) {
      this.fieldsVtMapClickSub = mapInstance.mapClick$.subscribe((event: MapClickEvent) =>
        this.handleFieldsVtMapClick(event),
      );
    }
  }

  /** Vector-tile hit-test at lat/lng (click only; popups are on markers). */
  private queryFieldVtAt(lat: number, lng: number): { field: Field; origProperties?: any } | null {
    const vt = this.fieldsVtPointer;
    if (!vt || !this.mapInstance) {
      return null;
    }

    const leafletMap = this.mapInstance.getMapInstance?.();
    if (!leafletMap) return null;

    const zoom = Math.floor(leafletMap.getZoom());
    const tc = latLngToTileCoords(lat, lng, zoom);
    const tileData = vt.tileIndex.getTile(tc.z, tc.x, tc.y);
    if (!tileData?.features?.length) {
      return null;
    }

    const pixel = latLngToTilePixel(lat, lng, tc.z, tc.x, tc.y, FIELD_VT_TILE_EXTENT);
    const hit = findFeatureAtPoint(tileData.features, pixel.x, pixel.y, vt.featuresById, 50);
    if (!hit) {
      return null;
    }

    const orig = hit.originalFeature;
    const props = (orig?.properties ?? {}) as Record<string, unknown>;
    let field =
      (props['_field'] as Field | undefined) ??
      (((hit.tileFeature?.tags ?? hit.tileFeature) as any)?.['_field'] as Field | undefined) ??
      null;

    if (!field) {
      const tags = (hit.tileFeature?.tags ?? {}) as Record<string, unknown>;
      const idStr = String(props['fieldIdStr'] ?? tags['fieldIdStr'] ?? '').trim();
      if (idStr) {
        const fromList = this.fields$.value.find((f) => String(f.id) === idStr);
        field = (fromList ?? ({id: idStr} as Field)) as Field;
      } else {
        const rawFid = props['fieldId'] ?? tags['fieldId'];
        if (rawFid != null && String(rawFid).trim() !== '') {
          const legacyId = String(rawFid).trim();
          const fromList = this.fields$.value.find((f) => String(f.id) === legacyId);
          field = (fromList ?? ({id: legacyId} as Field)) as Field;
        }
      }
    }

    if (!field?.id) {
      return null;
    }

    return { field, origProperties: orig?.properties };
  }

  /**
   * Track zoom on `mapZoomChange$`; debounce redraw on `mapMove$` (includes moveend after zoom).
   */
  private setupViewportRedrawListeners(): void {
    if (!this.mapInstance || this.mapViewportRedrawSub) {
      return;
    }

    const m = this.mapInstance;
    const sub = new Subscription();

    if (m.mapZoomChange$) {
      sub.add(
        m.mapZoomChange$.subscribe((zoom: number) => {
          // Do not call updateDisplay here: mapMove$ after zoom already redraws (avoids double clear / cluster-click flash).
          this.currentZoom = zoom;
        }),
      );
    }

    if (m.mapMove$) {
      sub.add(
        m.mapMove$.pipe(debounceTime(120)).subscribe((ev: MapMoveEvent) => {
          if (typeof ev.zoom === 'number') {
            this.currentZoom = ev.zoom;
          }
          this.updateDisplay();
        }),
      );
    }

    this.mapViewportRedrawSub = sub;
  }

  /**
   * Load fields from API
   */
  /** Map/bbox query: filters plus optional `seasonScope` (same tags as field list: nofields, plan, fact). */
  private toFieldMapQuery(query?: Record<string, unknown>): Record<string, unknown> {
    if (!query) {
      return {};
    }
    const filters = Array.isArray(query['filters']) ? query['filters'] : undefined;
    const seasonScope = Array.isArray(query['seasonScope']) ? query['seasonScope'] : undefined;
    return {
      ...(filters ? { filters } : {}),
      ...(seasonScope?.length ? { seasonScope } : {}),
    };
  }

  loadField(
    params: {
      west: number;
      east: number;
      south: number;
      north: number;
      query?: {};
    },
    center?: boolean,
  ): Observable<Field[]> {
    const query = params.query as Record<string, unknown> | undefined;
    const mapQuery = this.toFieldMapQuery(query);
    const useMapLoading = !this.clusterGroupId;

    setTimeout(() => {
      if (this.mapInstance && useMapLoading) {
        this.mapInstance.isLoading = true;
      }
    });

    const explicitFieldFilter =
      // @ts-ignore
      query && query['filters'] && Array.isArray(query['filters'])
        ? // @ts-ignore
          query['filters'].find((f: any) => f && (f.filterField === 'id' || f.filterField === 'name'))
        : null;

    if (explicitFieldFilter) {
      // Request fields, so single selected field will always be returned
      return this.fieldService.cropMonitoringFieldGetByQueryPost({
        filters: [explicitFieldFilter],
        ...(mapQuery['seasonScope'] ? {seasonScope: mapQuery['seasonScope'] as string[]} : {}),
      } as never).pipe(
        finalize(() => {
          if (this.mapInstance && useMapLoading) {
            this.mapInstance.isLoading = false;
          }
        }),
        map((fields: Field[]) => {
          const nameFilterValue = explicitFieldFilter?.filterValue || null;
          this.fields$.next(fields || []);

          const nameFilterChanged = nameFilterValue !== this.lastNameFilterValue;
          this.lastNameFilterValue = nameFilterValue;

          if (center || nameFilterChanged) {
            this.centerOnFieldsOrDefaultZoom(fields || []);
          }

          return fields || [];
        }),
      );
    }

    // Default behavior: request fields by bounding box
    return this.fieldService.cropMonitoringFieldGetFieldsByCoordinatesPost({
      west: params.west,
      east: params.east,
      south: params.south,
      north: params.north,
      query: mapQuery,
    } as never).pipe(
      finalize(() => {
        if (this.mapInstance && useMapLoading) {
          this.mapInstance.isLoading = false;
        }
      }),
      map((fields: Field[]) => {
        let nameFilterValue: string | null = null;

        // @ts-ignore
        if (query && query['filters'] && Array.isArray(query['filters'])) {
          // @ts-ignore
          const nameFilter = query['filters'].find(
            (filter: any) => filter.filterField === 'name' || filter.filterField === 'id',
          );
          nameFilterValue = nameFilter?.filterValue || null;
        }

        this.fields$.next(fields);

        const nameFilterChanged = nameFilterValue !== this.lastNameFilterValue;
        this.lastNameFilterValue = nameFilterValue;

        if (center || nameFilterChanged) {
          this.centerOnFieldsOrDefaultZoom(fields);
        }
        return fields;
      }),
    );
  }

  /**
   * Remove field markers and the geojson-vt layer from the map (does not clear `fields$`).
   * Same idea as SeasonManager.clearSeasonLayers.
   */
  private clearFieldVisualLayersOnly(): void {
    if (!this.mapInstance) {
      return;
    }
    this.removeFieldsVectorTileLayer();

    const markerIds = Array.from(this.managedMarkerIds);
    if (markerIds.length) {
      this.mapInstance.mapController$.next({ removeMarkers: markerIds });
    }
    this.managedMarkerIds.clear();
  }

  /**
   * Full clear + redraw to avoid ghost polygons and inconsistent ids (mirrors SeasonManager.updateDisplay).
   */
  private updateDisplay(): void {
    if (!this.mapInstance) {
      return;
    }

    this.syncZoomFromMapIfPossible();

    const allFields = this.fields$.value ?? [];
    const fields = this.filterFieldsByViewport(allFields);

    this.clearFieldVisualLayersOnly();

    if (!fields.length) {
      return;
    }

    if (this.currentZoom < FieldManager.ZOOM_DETAIL_MIN) {
      return;
    }

    if (this.currentZoom < this.zoomThreshold) {
      this.renderFieldPointsBand(fields);
      return;
    }

    this.renderFieldPolygonsOnly(fields);
    this.renderFieldPointsBand(fields);
  }

  /** Keep `currentZoom` in sync with Leaflet before the first map event. */
  private syncZoomFromMapIfPossible(): void {
    try {
      const z = this.mapInstance?.getMapInstance?.()?.getZoom?.();
      if (typeof z === 'number' && !Number.isNaN(z)) {
        this.currentZoom = z;
      }
    } catch {
      /* ignore */
    }
  }

  /** Fields whose centroid lies inside the padded map bounds. */
  private filterFieldsByViewport(fields: Field[]): Field[] {
    return filterByViewportBounds(fields, this.mapInstance?.getMapInstance?.(), FIELD_VIEWPORT_BOUNDS_PAD, (f) =>
      this.getFieldRepresentativeLatLng(f),
    );
  }

  /** Marker position for viewport tests (polygon centroid). */
  private getFieldRepresentativeLatLng(field: Field): unknown | null {
    if (!field?.polygonJson) {
      return null;
    }
    const coords = PolygonConverterUtil.polygonJsonToCoordinates(field.polygonJson);
    if (!coords?.length) {
      return null;
    }

    const c = PolygonConverterUtil.getPolygonCenter(coords);
    if (!c || typeof L === 'undefined' || typeof L.latLng !== 'function') {
      return null;
    }
    return L.latLng(c.lat, c.lng);
  }

  /** At high zoom: render geojson-vt accent-fill polygons (markers added separately). */
  private renderFieldPolygonsOnly(fields: Field[]): void {
    if (!this.mapInstance) {
      return;
    }
    this.renderFieldPolygonsVectorTiles(fields);
  }

  /** Centroid markers for viewport-filtered fields → mergeMarkers + clustering. */
  private renderFieldPointsBand(fields: Field[]): void {
    if (!this.mapInstance) {
      return;
    }

    const markers = fields
      .filter((field) => field?.polygonJson)
      .map((field) => this.createMarkerFromFieldPolygon(field))
      .filter((marker): marker is NonNullable<typeof marker> => marker !== null);

    markers.forEach((marker) => this.managedMarkerIds.add(marker.id));

    if (markers.length) {
      this.mapInstance.mapController$.next({ mergeMarkers: markers });
    }
  }

  private cropDisplayName(cropId: string | null | undefined): string {
    if (cropId == null || String(cropId).length === 0) {
      return '—';
    }
    const raw = this.cropCacheService.getCropNameById(String(cropId), true) ?? String(cropId);
    return escapePopupHtml(raw);
  }

  /**
   * Cluster mode: the `GetFieldsByRegions` response already carries `firm`, so the same
   * red legal-address pin is drawn straight from it and the camera moves to the firm.
   * The pin is drawn before centering, so the polygon-mode reload the `setCenter`
   * triggers finds it already in place and skips its own firm request.
   */
  private showFirmMarkerFromClusterResponse(firm: FirmSmallDto | null | undefined): void {
    const coords = firm?.locationJson ? parseWktPoint(firm.locationJson) : null;
    if (!coords) {
      return;
    }

    this.updateFirmLegalAddressPin(firm);
    this.mapInstance?.mapController$.next({
      setCenter: { lat: coords.lat, lng: coords.lng, zoom: 12 },
    });
  }

  /** Firm id of the legal-address pin currently on the map, or null when there is none. */
  getFirmLegalAddressPinFirmId(): string | null {
    return this.firmPinFirmId;
  }

  /**
   * Manufacturer legal-address pin: red pin marker with a click popup (name, address,
   * link to the firm card). Pass null / a firm without coordinates to remove the pin.
   * Used in both map modes — cluster mode feeds it from the `GetFieldsByRegions`
   * response, polygon mode from a dedicated firm request.
   */
  updateFirmLegalAddressPin(firm: FirmSmallDto | null | undefined): void {
    if (!this.mapInstance) {
      return;
    }

    const coords = firm?.locationJson ? parseWktPoint(firm.locationJson) : null;

    if (!firm?.id || !coords) {
      if (this.firmPinFirmId) {
        this.mapInstance.mapController$.next({
          removeMarkers: [this.buildFirmPinMarkerId(this.firmPinFirmId)],
        });
        this.firmPinFirmId = null;
      }
      return;
    }

    // Producer filter switched while the pin was shown — drop the previous one.
    if (this.firmPinFirmId && this.firmPinFirmId !== firm.id) {
      this.mapInstance.mapController$.next({
        removeMarkers: [this.buildFirmPinMarkerId(this.firmPinFirmId)],
      });
    }

    this.ensureFirmPinPane();
    this.bindFirmPinPopupLinkHandler();

    const marker: PanedMarker = {
      id: this.buildFirmPinMarkerId(firm.id),
      lat: coords.lat,
      lng: coords.lng,
      title: firm.name ?? '',
      popup: this.buildFirmPinPopupHtml(firm),
      openPopupOnHover: false,
      withCluster: false,
      customIcon: buildGroundSurveyPinDivIconHtml(FIRM_PIN_COLOR),
      customIconSize: [25, 41],
      customIconAnchor: [12, 41],
      customIconPopupAnchor: [1, -34],
      pane: FIRM_PIN_PANE,
      data: { firm },
      onClick: () => {
        /* popup opens natively via bindPopup */
      },
    };

    this.firmPinFirmId = firm.id;
    this.mapInstance.mapController$.next({ mergeMarkers: [marker] });
  }

  private buildFirmPinMarkerId(firmId: string): string {
    return `firm-pin-${firmId}`;
  }

  private buildFirmPinPopupHtml(firm: FirmSmallDto): string {
    const name = escapePopupHtml(firm.name);
    const address = escapePopupHtml(firm.physicalAddress);
    const cardUrl = this.router.serializeUrl(this.router.createUrlTree([FIRM_CARD_ROUTE, firm.id]));
    const linkLabel = escapePopupHtml(this.translate.instant('GO_TO_MANUFACTURER_CARD'));
    const link =
      `<a class="${FIRM_PIN_POPUP_LINK_CLASS}" data-firm-id="${escapePopupHtml(firm.id)}" ` +
      `href="${escapePopupHtml(cardUrl)}">${linkLabel}</a>`;
    return `<strong>${name}</strong>${address ? `<br>${address}` : ''}<br>${link}`;
  }

  /** Dedicated pane so the pin stays above raster overlays and field polygons. */
  private ensureFirmPinPane(): void {
    const map = this.mapInstance?.getMapInstance?.();
    if (!map || map.getPane(FIRM_PIN_PANE)) {
      return;
    }
    const pane = map.createPane(FIRM_PIN_PANE);
    pane.style.zIndex = String(FIRM_PIN_PANE_Z_INDEX);
  }

  /**
   * SPA navigation for the "manufacturer card" link inside the raw-HTML Leaflet popup.
   * Bound to the map (not to `document`), so the handler goes away with the map instance.
   */
  private bindFirmPinPopupLinkHandler(): void {
    const map = this.mapInstance?.getMapInstance?.();
    if (!map || this.firmPinPopupHandlerMap === map) {
      return;
    }
    this.firmPinPopupHandlerMap = map;

    map.on('popupopen', (event: any) => {
      const anchor = event?.popup
        ?.getElement?.()
        ?.querySelector?.(`a.${FIRM_PIN_POPUP_LINK_CLASS}`) as HTMLAnchorElement | null;
      const firmId = anchor?.dataset?.['firmId'];
      if (!anchor || !firmId) {
        return;
      }
      // Assignment, not addEventListener: a reopened popup must not stack handlers.
      anchor.onclick = (clickEvent: MouseEvent) => {
        clickEvent.preventDefault();
        this.ngZone.run(() => this.router.navigate([FIRM_CARD_ROUTE, firmId]));
      };
    });
  }

  private buildFieldPopupHtml(field: Field): string {
    const fi = field as any;
    const name = escapePopupHtml(field.name || this.translate.instant('FIELD'));
    let html = `<b>${name}</b>`;
    const cid = fi.cropId as string | undefined;
    if (cid) {
      html += `<br><small>${escapePopupHtml(this.translate.instant('CROP_ID'))}: ${this.cropDisplayName(cid)}</small>`;
    }
    return html;
  }

  /** Single accent color for field markers (no per-crop palette on the map). */
  private resolveFieldCropColor(_field: Field): string {
    return this.accentColor;
  }

  /** SVG markers + markercluster (input list is already viewport-filtered). */
  private buildFieldMarkerConfig(field: Field, lat: number, lng: number, markerId: string): any {
    const color = this.resolveFieldCropColor(field);
    const marker: any = {
      id: markerId,
      lat,
      lng,
      title: field.name || this.translate.instant('FIELD'),
      popup: this.buildFieldPopupHtml(field),
      openPopupOnHover: true,
      data: { field },
      color,
      withCluster: true,
      clusterGroupId: this.clusterGroupId ?? undefined,
      clusterGroupAccent: this.clusterGroupId ? this.accentColor : undefined,
      ...(this.markerClusterPane ? {pane: this.markerClusterPane} : {}),
      onClick: () => {
        try {
          const eventsController = this.mapInstance?.getEventsController?.();
          if (eventsController?.emitMarkerClick) {
            eventsController.emitMarkerClick({
              id: marker.id,
              lat: marker.lat,
              lng: marker.lng,
              data: marker.data,
            });
          }
          this.fieldMarkerClick$.next({
            lat: marker.lat,
            lng: marker.lng,
            data: marker.data,
          });
        } catch (err) {
          console.warn('Failed to emit field marker click', err);
        }
      },
    };
    return marker;
  }

  /** Build centroid marker (same pattern as seasons). */
  private createMarkerFromFieldPolygon(field: Field): any {
    if (!field?.polygonJson || field.id == null) {
      return null;
    }
    const coordinates = PolygonConverterUtil.polygonJsonToCoordinates(field.polygonJson);
    if (!coordinates?.length) {
      return null;
    }
    const center = PolygonConverterUtil.getPolygonCenter(coordinates);
    if (!center) {
      return null;
    }
    return this.buildFieldMarkerConfig(field, center.lat, center.lng, `field_marker_${field.id}`);
  }

  /** Remove geojson-vt canvas layer from the map, including tiles whose handle was lost. */
  private removeFieldsVectorTileLayer(): void {
    this.fieldsVtOpId++;
    clearLeafletDataPane(this.mapInstance?.getMapInstance?.(), this.vectorTilesPane);
    this.fieldsVtLayer = null;
    this.fieldsVtPointer = null;
    this.fieldsVtThinStroke = false;
  }

  private ensureFieldsVtPane(map: any): void {
    const name = this.vectorTilesPane;
    if (!map.getPane(name)) {
      const pane = map.createPane(name);
      pane.style.pointerEvents = 'none';
    }
  }

  private buildFieldsFeatureCollection(fields: Field[]): GeoJSON.FeatureCollection | null {
    const features: GeoJSON.Feature[] = [];
    for (const field of fields) {
      if (field?.polygonJson == null || field.id == null) continue;
      const coordinates = PolygonConverterUtil.polygonJsonToCoordinates(field.polygonJson);
      if (!PolygonConverterUtil.isValidPolygon(coordinates)) continue;

      const ringLngLat = coordinates.map(([lat, lng]) => [lng, lat] as [number, number]);
      const fr = ringLngLat[0];
      const lr = ringLngLat[ringLngLat.length - 1];
      if (fr[0] !== lr[0] || fr[1] !== lr[1]) {
        ringLngLat.push([fr[0], fr[1]]);
      }

      const fillColor = this.accentColor;
      const fieldIdNum = this.fieldVtNumericId(field);

      features.push({
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates: [ringLngLat],
        },
        properties: {
          fieldId: fieldIdNum,
          fieldIdStr: String(field.id),
          fillColor,
          strokeColor: FIELD_POLYGON_STROKE,
          fillOpacity: this.polygonVtFillOpacity ?? 0.8,
          strokeWidth: this.polygonVtStrokeWidth ?? 2,
          strokeDashArray: this.polygonVtStrokeDashArray ?? undefined,
          _field: field,
        },
      });
    }

    if (!features.length) {
      return null;
    }

    return { type: 'FeatureCollection', features };
  }

  private renderFieldPolygonsVectorTiles(fields: Field[]): void {
    const leafletMap = this.mapInstance?.getMapInstance?.();
    if (!leafletMap) return;

    const fc = this.buildFieldsFeatureCollection(fields);
    if (!fc?.features?.length) return;

    const opId = ++this.fieldsVtOpId;
    const featureCount = fc.features.length;
    const mgr = this;

    this.ngZone.runOutsideAngular(() => {
      try {
        const tolerance = featureCount > 20000 ? 5 : 3;
        const tileIndex = geojsonvt(fc as any, {
          maxZoom: FIELD_GEOJSON_VT_OPTIONS.maxZoom,
          extent: FIELD_GEOJSON_VT_OPTIONS.extent,
          buffer: FIELD_GEOJSON_VT_OPTIONS.buffer,
          lineMetrics: FIELD_GEOJSON_VT_OPTIONS.lineMetrics,
          promoteId: FIELD_GEOJSON_VT_OPTIONS.promoteId,
          generateId: FIELD_GEOJSON_VT_OPTIONS.generateId,
          indexMaxZoom: FIELD_GEOJSON_VT_OPTIONS.indexMaxZoom,
          indexMaxPoints: FIELD_GEOJSON_VT_OPTIONS.indexMaxPoints,
          tolerance,
        });

        const featuresById = buildVtOriginalFeaturesByNumericId(fc.features as any[], 'fieldId');

        const layer = mgr.createFieldsVectorGridLayer(tileIndex, featureCount);
        const vtPtr = { tileIndex, featuresById };

        mgr.ngZone.run(() => {
          if (opId !== mgr.fieldsVtOpId) return;
          const map = mgr.mapInstance?.getMapInstance?.();
          if (!map) return;
          mgr.ensureFieldsVtPane(map);
          map.addLayer(layer);
          mgr.fieldsVtLayer = layer;
          mgr.fieldsVtPointer = vtPtr;
        });
      } catch (e) {
        console.warn('FieldManager: geojson-vt rendering failed', e);
      }
    });
  }

  /** Per-tile canvas layer (same approach as GeoFileLoaderManager). */
  private createFieldsVectorGridLayer(tileIndex: any, featureCount: number): any {
    this.fieldsVtThinStroke = featureCount > 20000;
    const mgr = this;

    const VectorTileLayer = L.GridLayer.extend({
      createTile: (coords: any) => {
        const tile = document.createElement('canvas');
        const tileSize = 256;
        tile.width = tileSize;
        tile.height = tileSize;
        tile.style.pointerEvents = 'none';
        const ctx = tile.getContext('2d');
        if (!ctx) return tile;
        mgr.paintFieldsVtTile(ctx, tileIndex, coords);
        return tile;
      },
    });

    return new VectorTileLayer({
      pane: this.vectorTilesPane,
      tileSize: 256,
      updateWhenZooming: false,
      updateWhenIdle: true,
      keepBuffer: 2,
    });
  }

  private paintFieldsVtTile(ctx: CanvasRenderingContext2D, tileIndex: any, coords: {x: number; y: number; z: number}): void {
    const tileData = tileIndex.getTile(coords.z, coords.x, coords.y);
    if (!tileData?.features?.length) {
      return;
    }
    const scale = 256 / FIELD_VT_TILE_EXTENT;
    for (const feature of tileData.features) {
      ctx.save();
      paintVtPolygonOnCanvasTile(
        ctx,
        feature,
        scale,
        this.fieldsVtThinStroke,
        FIELD_POLYGON_STROKE,
        this.accentColor,
        this.polygonVtFillOpacity,
      );
      ctx.restore();
    }
  }

  private repaintFieldsVtCanvases(): void {
    const tileIndex = this.fieldsVtPointer?.tileIndex;
    if (!this.fieldsVtLayer || !tileIndex) {
      return;
    }
    repaintLeafletGridLayerCanvases(this.fieldsVtLayer, (ctx, coords) => {
      this.paintFieldsVtTile(ctx, tileIndex, coords);
    });
  }

  /** Numeric id for geojson-vt `promoteId` and map hit-testing (string ids are hashed). */
  private fieldVtNumericId(field: Field): number {
    const raw = field.id as unknown;
    if (typeof raw === 'number' && Number.isFinite(raw)) {
      return raw;
    }
    if (typeof raw === 'string' && raw.trim().length) {
      const n = Number(raw);
      if (Number.isFinite(n) && String(n) === raw.trim()) {
        return n;
      }
    }
    const nFallback = Number(raw);
    if (Number.isFinite(nFallback)) {
      return nFallback;
    }
    const s = String(raw ?? '');
    let h = 0;
    for (let i = 0; i < s.length; i++) {
      h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
    }
    const id = Math.abs(h) >>> 0;
    return id || 1;
  }

  private handleFieldsVtMapClick(ev: MapClickEvent): void {
    if (
      this.currentZoom < FieldManager.ZOOM_POLYGONS_ONLY_MIN ||
      this.currentZoom < FieldManager.ZOOM_DETAIL_MIN ||
      !this.fieldsVtPointer
    ) {
      return;
    }
    const hit = this.queryFieldVtAt(ev.lat, ev.lng);
    if (!hit) {
      return;
    }

    this.fieldPolygonClick$.next({
      properties: hit.origProperties,
      data: { fieldId: hit.field.id, field: hit.field },
      lat: ev.lat,
      lng: ev.lng,
    });
  }
  /**
   * Clear map layers and reset in-memory field list (like SeasonManager.clearSeasons).
   */
  clearFieldLayers(): void {
    if (!this.mapInstance) {
      return;
    }
    this.clearFieldVisualLayersOnly();
    this.updateFirmLegalAddressPin(null);
    this.fields$.next([]);
  }

  /**
   * Clear fields from state
   */
  clearFields(): void {
    this.fields$.next([]);
    this.clearFieldLayers();
  }

  getRegionsFieldCount(query?: {}): Observable<GisCircleMarker[]> {
    // @ts-ignore
    if (query && query['filters'] && Array.isArray(query['filters'])) {
      // @ts-ignore
      query['filters'] = query['filters'].filter(
        (f: any) => f.filterField !== 'sensor' && f.filterField !== 'cadastre',
      );
    }

    // Get region IDs from the region manager's cache
    let regionIds = this.regionManager.getCachedRegionIds();

    // Check if there's a regionId filter in the query
    // @ts-ignore
    if (query && query['filters'] && Array.isArray(query['filters'])) {
      // @ts-ignore
      const regionFilter = query['filters'].find((filter: any) => filter.filterField === 'regionId');

      if (regionFilter && regionFilter.filterValue) {
        regionIds = regionFilter.filterValue;
      }
    }

    return this.fieldService
      .cropMonitoringFieldGetFieldsByRegionsPost({
        regionIds: regionIds,
        query: this.toFieldMapQuery(query as Record<string, unknown> | undefined),
      } as never)
      .pipe(
        tap((response: FieldsByRegionsDto) => this.showFirmMarkerFromClusterResponse(response.firm)),
        map((response: FieldsByRegionsDto) => {
          const fieldsByRegion = response.fields ?? {};

          // Check if only one region with count = 1
          const entries = Object.entries(fieldsByRegion);
          if (entries.length === 1 && entries[0][1] === 1) {
            // Load the single field - use bounding box from region or default
            const regionId = entries[0][0];
            const regionPolygon = this.regionManager.getRegionPolygonsCache().get(regionId);
            if (regionPolygon) {
              const bounds = this.calculateBoundsFromPolygon(regionPolygon?.polygon);

              const unSub = this.loadField(
                {
                  west: bounds.west,
                  east: bounds.east,
                  south: bounds.south,
                  north: bounds.north,
                  query: query,
                },
                true,
              ).subscribe(() => {
                if (this.mapInstance) {
                  this.mapInstance.isLoading = false;
                }
                unSub.unsubscribe();
              });
            }
            return [];
          }

          return createRegionCircleMarkers(
            fieldsByRegion,
            this.regionManager.getRegionPolygonsCache(),
            this.accentColor,
            this.mapInstance,
          );
        }),
      );
  }

  private centerOnFieldsOrDefaultZoom(fields: Field[]): void {
    if (!this.mapInstance) return;
    if (fields.length === 1 && fields[0]?.polygonJson) {
      const coordinates = PolygonConverterUtil.polygonJsonToCoordinates(fields[0].polygonJson);
      const center = coordinates?.length ? PolygonConverterUtil.getPolygonCenter(coordinates) : null;
      if (center) {
        this.mapInstance.mapController$.next({
          setCenter: { lat: center.lat, lng: center.lng, zoom: 16 },
        });
      }
    }
  }

  private calculateBoundsFromPolygon(polygon: any): { west: number; east: number; south: number; north: number } {
    const coordinates: any[] = polygon?.coordinates;
    let west = Infinity,
      east = -Infinity,
      south = Infinity,
      north = -Infinity;

    coordinates?.forEach(([lat, lng]) => {
      if (lng < west) west = lng;
      if (lng > east) east = lng;
      if (lat < south) south = lat;
      if (lat > north) north = lat;
    });

    return { west, east, south, north };
  }
}
